import { get } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useQuery, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";
import { minutesToMilliseconds } from "date-fns";
import { z } from "zod";

import { supportQueryKeys } from "./queryKeys";

const STALE_TIME_MINUTES = 5;
const PAGE_SIZE = 50;

const scoreLogSchema = z.object({
  id: z.number(),
  shiftId: z.string().nullish(),
  action: z.string(),
  displayAction: z.string().nullish(),
  reason: z.string().nullish(),
  facilityName: z.string().nullish(),
  createdAt: z.string(),
  clipboardScoreUpdatedScore: z.number().nullish(),
  clipboardScoreAdjustment: z.number().nullish(),
  clipboardScoreMultiplier: z.string(),
  acceptedDispute: z.boolean().nullish(),
});

const scoreHistoryResponseSchema = z.object({
  scoreLogs: z.array(scoreLogSchema),
});

type ScoreHistoryResponse = z.infer<typeof scoreHistoryResponseSchema>;

export interface ScoreLedgerEntry {
  readonly id: number;
  readonly action: string;
  readonly displayAction: string;
  readonly reason: string | undefined;
  readonly adjustment: number;
  readonly multiplier: string;
  readonly resultingScore: number | undefined;
  readonly createdAt: string;
  readonly isDisputed: boolean;
}

interface UseShiftScoreLedgerParams {
  readonly workerId: string;
  readonly shiftId: string;
}

export function useShiftScoreLedger(
  params: UseShiftScoreLedgerParams,
  options: UseQueryOptions<ScoreLedgerEntry[]> = {}
): UseQueryResult<ScoreLedgerEntry[]> {
  const { workerId, shiftId } = params;
  const url = `${environmentConfig.REACT_APP_ATTENDANCE_SCORE_SERVICE_URL}/scores/${workerId}/history`;

  return useQuery({
    queryKey: supportQueryKeys.scoreLedger(workerId, shiftId),
    queryFn: async () => {
      const response = await get({
        url,
        responseSchema: scoreHistoryResponseSchema,
        queryParams: {
          pageSize: PAGE_SIZE,
          filter: {
            shiftIds: [shiftId],
          },
        },
      });

      return transformScoreLogs(response.data);
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_ATTENDANCE_SCORE_HISTORY,
      userErrorMessage: "Unable to load score history for this shift.",
    },
    enabled: Boolean(workerId) && Boolean(shiftId),
    staleTime: minutesToMilliseconds(STALE_TIME_MINUTES),
    ...options,
  });
}

const ACTION_LABELS: Record<string, string> = {
  WORK_SHIFT: "Worked Shift",
  CANCEL_SHIFT: "Cancellation",
  NO_CALL_NO_SHOW: "No Show",
  CORRECT_SCORE: "Score Correction",
  ACCOUNT_RE_ENROLL: "Account Re-enrolled",
  START_SHIFT_LATE: "Shift Started Late",
  CLOCKIN_TIMELINESS_BONUS: "Clocked in On Time",
  QUALITY_BONUS: "Excellence Bonus",
  CLIPBOARD_SCORE_LAUNCH: "Clipboard Score Launch",
  WORKER_LEFT_EARLY_WITHOUT_PERMISSION: "Shift Abandonment",
  ORIENTATION_PASSED: "Demo shift completed",
} as const;

const HIDDEN_ACTIONS = new Set(["ORIENTATION_ACTIVATION", "ORIENTATION_DEACTIVATION"]);

function transformScoreLogs(response: ScoreHistoryResponse): ScoreLedgerEntry[] {
  return response.scoreLogs.filter(isVisibleAction).map(mapScoreLogToEntry);
}

function isVisibleAction(log: z.infer<typeof scoreLogSchema>): boolean {
  return !HIDDEN_ACTIONS.has(log.action);
}

function mapScoreLogToEntry(log: z.infer<typeof scoreLogSchema>): ScoreLedgerEntry {
  return {
    id: log.id,
    action: log.action,
    // Prioritize our human-readable labels over API's displayAction (which is often the raw enum)
    displayAction: ACTION_LABELS[log.action] ?? formatActionLabel(log.action),
    reason: log.reason ?? undefined,
    adjustment: log.clipboardScoreAdjustment ?? 0,
    multiplier: log.clipboardScoreMultiplier,
    resultingScore: log.clipboardScoreUpdatedScore ?? undefined,
    createdAt: log.createdAt,
    isDisputed: log.acceptedDispute ?? false,
  };
}

/**
 * Converts a SCREAMING_SNAKE_CASE action to Title Case.
 * Example: "START_SHIFT_LATE" -> "Start Shift Late"
 */
function formatActionLabel(action: string): string {
  return action
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
